import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { PACKAGES } from "@/config/brand";
import { useTracking } from "@/hooks/useTracking";
import { Reveal } from "./Reveal";

const PackageCard = ({ pkg, index }) => {
  const { withTracking } = useTracking();
  const highlighted = pkg.highlighted;

  return (
    <Reveal delay={0.12 + index * 0.12}>
      <motion.div
        whileHover={{ y: -6 }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
        data-testid={`package-card-${pkg.id}`}
        className={`relative flex h-full flex-col overflow-hidden rounded-3xl p-7 md:p-9 ${
          highlighted
            ? "bg-sdh-sky text-[#0B1D39] shadow-[0_30px_70px_-25px_rgba(71,188,234,0.5)]"
            : "border border-white/12 bg-white/[0.05] text-white backdrop-blur-sm"
        }`}
      >
        {highlighted && (
          <span
            data-testid="complete-package-badge"
            className="absolute right-5 top-5 rounded-full bg-[#0B1D39] px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-sdh-sky"
          >
            Most Popular
          </span>
        )}
        {highlighted && (
          <div
            aria-hidden="true"
            data-testid="full-package-arrow"
            className="pointer-events-none absolute -top-3 right-8 z-10 flex -translate-y-full -rotate-6 flex-col items-center"
          >
            <span className="font-display text-lg uppercase tracking-wider text-sdh-sky">
              the full package
            </span>
            <svg width="54" height="36" viewBox="0 0 54 36" fill="none">
              <motion.path
                d="M27 2 C27 14 27 20 27 28 M18 21 L27 30 L36 21"
                stroke="#47BCEA"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 0.7, ease: "easeOut" }}
              />
            </svg>
          </div>
        )}
        <p
          className={`text-xs font-bold uppercase tracking-[0.25em] ${
            highlighted ? "text-[#0B1D39]/70" : "text-sdh-sky"
          }`}
        >
          Package {pkg.order}
        </p>
        <h3 className="mt-2 font-display text-3xl uppercase tracking-wide md:text-4xl">
          {pkg.name}
        </h3>
        <div className="mt-3 flex items-end gap-3">
          <p
            className="font-display text-6xl tracking-tight md:text-7xl"
            data-testid={`package-price-${pkg.id}`}
          >
            {pkg.price}
          </p>
          <span
            data-testid={`package-gst-${pkg.id}`}
            className={`mb-2 rounded-full px-3 py-1.5 font-display text-xl uppercase tracking-wide ${
              highlighted ? "bg-[#0B1D39]/15 text-[#0B1D39]" : "bg-sdh-sky/15 text-sdh-sky"
            }`}
          >
            + GST
          </span>
        </div>
        <p
          className={`mt-1 text-xs font-bold uppercase tracking-[0.22em] ${
            highlighted ? "text-[#0B1D39]/60" : "text-white/50"
          }`}
        >
          Fixed Price · Any Bathroom Size
        </p>
        <p
          className={`mt-5 text-sm leading-relaxed ${
            highlighted ? "text-[#0B1D39]/80" : "text-white/70"
          }`}
        >
          {pkg.description}
        </p>
        <ul className="mt-5 space-y-2">
          {pkg.cardPoints.map((point) => (
            <li
              key={point}
              className={`flex items-start gap-2.5 text-sm font-medium ${
                highlighted ? "text-[#0B1D39]" : "text-white/85"
              }`}
            >
              <Check
                size={16}
                className={`mt-0.5 shrink-0 ${highlighted ? "text-[#0B1D39]" : "text-sdh-sky"}`}
              />
              {point}
            </li>
          ))}
        </ul>
        <div className="mt-auto pt-8">
          <Link
            to={withTracking(pkg.route)}
            data-testid={`explore-${pkg.id}-button`}
            className={`group inline-flex w-full items-center justify-center gap-2.5 rounded-full px-6 py-4 font-display text-lg uppercase tracking-wider transition-colors duration-300 ${
              highlighted
                ? "cta-pulse-navy bg-[#0B1D39] text-white hover:bg-[#071735]"
                : "cta-pulse-sky bg-sdh-sky text-[#0B1D39] hover:bg-white"
            }`}
          >
            {pkg.cta}
            <ArrowRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </Link>
        </div>
      </motion.div>
    </Reveal>
  );
};

const PackageGrid = () => (
  <div
    id="packages"
    data-testid="packages-section"
    className="grid gap-5 md:grid-cols-2 md:gap-7"
  >
    <PackageCard pkg={PACKAGES["labour-only"]} index={0} />
    <PackageCard pkg={PACKAGES["complete-package"]} index={1} />
  </div>
);

export default PackageGrid;
